import type { History } from "history";
import React from "react";

import {
  CDP_LAYOUT_EXPERIMENT_NAME,
  classDetailsPageId,
} from "../../shared/CdpLayoutExperiment";
import useExperiment from "./useExperiment";

/**
 * Exposure is tracked server side in `app.ts` for class details page loads;
 * triggering it from here would skew the sample ratio between arms. Other
 * call sites, the teacher profile among them, read the variant without
 * contributing an exposure.
 */
export function useCdpLayoutTreatment() {
  const { variant } = useExperiment(CDP_LAYOUT_EXPERIMENT_NAME);

  return variant === "treatment";
}

/**
 * Covers class details pages reached by client-side routing, which the server
 * trigger never sees. `history.listen` runs on navigation only, and before the
 * variant renders, so this does not track paint speed.
 *
 * The page recorded last is not recorded again. The hook starts out holding
 * the page the server recorded, so the canonical slug redirect that follows
 * the first render does not count twice.
 */
export function useCdpLayoutExposureOnClientNavigation(history: History) {
  const { trigger } = useExperiment(CDP_LAYOUT_EXPERIMENT_NAME);
  const lastExposedPageId = React.useRef(
    classDetailsPageId(window.location.pathname),
  );
  const currentPageRecorded = React.useRef(
    lastExposedPageId.current !== undefined,
  );

  React.useEffect(() => {
    return history.listen((location, action) => {
      const pageId = classDetailsPageId(location.pathname);

      // Anchors and query changes stay on the page, so they must not clear the
      // flag on their way out.
      if (pageId !== undefined && pageId === lastExposedPageId.current) {
        return;
      }

      if (action === "REPLACE" && currentPageRecorded.current) {
        // ActivityDetailPage rewrites a uid or a stale slug to the canonical
        // one once the activity resolves. Neither a uid nor a case folded slug
        // shares an id with what replaces it, so the check above would not
        // recognize the rewritten page as one already recorded.
        return;
      }

      if (pageId === undefined) {
        currentPageRecorded.current = false;
        return;
      }

      lastExposedPageId.current = pageId;
      currentPageRecorded.current = true;
      // The provider dedupes on these properties, so a distinct page per
      // trigger is what lets navigations after the first one through.
      trigger({
        additionalProperties: {
          class_details_page_id: pageId,
          page_path: location.pathname,
        },
      });
    });
  }, [history, trigger]);
}
